import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { CategorizedTableData, TABLE_CONFIG_BY_CATEGORY, TableConfig } from '../../common/common-ownership-table-view/linked-ownership';
import { MatDialog } from '@angular/material/dialog';
import { VaultStoreEffects } from '@app/root-store/vault-store/effects';
import { DocumentService } from '@app/shared/services';
import { ToastrService } from 'ngx-toastr';
import { AddDocumentComponent } from '../add-document.component';
import { NonAccessTemplateComponent } from '../linked-templates/view-template-list/non-access-template/non-access-template.component';
import { NotifyDocumentCreatedAddAsRolePlayers } from '@app/shared/services/notify-document-created-add-as-roleplayers/notify-document-created-add-as-roleplayers';
import { NotifyDocumentWhenLinkedPositionCreated } from '@app/shared/services/notify-document-when-linked-position-created/notify-document-when-linked-position-created';
import { CommonOwnershipTableViewComponent } from '../../common/common-ownership-table-view/common-ownership-table-view.component';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'linked-ownership-positions',
  templateUrl: './linked-ownership-positions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkedOwnershipPositionsComponent implements OnInit, OnDestroy {
  @ViewChildren(CommonOwnershipTableViewComponent) commonOwnershipTableComponents: QueryList<CommonOwnershipTableViewComponent>;
  @Input() mode;
  @Input() data;
  @Input() origin;
  @Input() formName;
  @Input() vaultFormId;
  @Input() currentuserId;
  @Input() currentFormId;
  @Input() isRemoveEditSaveSharing;
  @Input() linkedAssetsEnabled = false;
  @Input() linkedEntitiesEnabled = false;
  @Input() linkedLiabilitiesEnabled = false;
  @Output() linkedPositionSelectionChange = new EventEmitter<{
    record: any;
    index: number;
    isChecked: boolean;
    categoryKey: string;
  }>();

  stateObject;
  screenWidth: number;
  isDataLoading = true;
  isBuySellTemplate = false;
  hasAutoAddSetting = false;
  buySellTemplateId = '2181';
  allFormsForAssets: any[] = [];
  allFormsForEntities: any[] = [];
  allFormsForLiabilities: any[] = [];
  subscription = new Subscription();
  selectedRows: Set<number> = new Set();
  linkedPositionsList: CategorizedTableData[] = [];
  tableDisplayOrder: ('assets' | 'liabilities' | 'entities')[] = ['assets', 'liabilities', 'entities'];

  constructor(
    private dialog: MatDialog,
    private vaultService: VaultStoreEffects,
    private _notifyDocumentCreatedAddAsRolePlayers: NotifyDocumentCreatedAddAsRolePlayers,
    private _notifyDocumentWhenLinkedPositionCreated: NotifyDocumentWhenLinkedPositionCreated,
    private documentService: DocumentService,
    private toastr: ToastrService,
    private cd: ChangeDetectorRef
  ) {
    this.subscription.add(
      this.vaultService.vaultObject.subscribe((item) => {
        this.stateObject = item;
      })
    );

    this.subscription.add(
      this._notifyDocumentWhenLinkedPositionCreated.getMessage().subscribe((val) => {
        this.getVaultUserFormLinkedPositions();
        this.getAllUserFormsForLinkedPositions();
      })
    );
  }

  ngOnInit(): void {
    this.screenWidth = window.innerWidth;
    if (this.vaultFormId == this.buySellTemplateId) {
      this.isBuySellTemplate = true;
      this.hasAutoAddSetting = false;
      this.tableDisplayOrder = ['entities', 'liabilities', 'assets'];
    } else {
      this.hasAutoAddSetting = true;
    }
    this.getVaultUserFormLinkedPositions();
    this.getAllUserFormsForLinkedPositions();
  }

  getVaultUserFormLinkedPositions() {
    this.subscription.add(
      this.documentService.getVaultUserFormLinkedPositions(this.currentuserId ?? this.stateObject.userId, this.currentFormId).subscribe(
        (response) => {
          this.linkedPositionsList = this.buildAllLinkedPositions(response ?? []);
          this.isDataLoading = false;
          this.cd.detectChanges();
        },
        (error: any) => {
          this.toastr.error('Error occurred while getting linked positions', error?.error?.message);
          this.isDataLoading = false;
          this.cd.detectChanges();
        }
      )
    );
  }

  getAllUserFormsForLinkedPositions() {
    const linkedPositionsFormIds = this.stateObject?.forms.map((form) => form.id);
    const payload = {
      vault_user_form_id: this.currentFormId,
      formids: linkedPositionsFormIds,
    };
    this.subscription.add(
      this.documentService.getAllUserFormsForLinkedPositions(payload).subscribe(
        (response) => {
          this.allFormsForAssets = response.filter((form) => this.isTrueFlag(form.is_asset) && !this.isTrueFlag(form.is_liability));
          this.allFormsForLiabilities = response.filter((form) => !this.isTrueFlag(form.is_asset) && this.isTrueFlag(form.is_liability));
          this.allFormsForEntities = response.filter(
            (form) => !this.isTrueFlag(form.is_asset) && !this.isTrueFlag(form.is_liability) && form.organization_section === 'Entities'
          );

          if (this.linkedPositionsList.length > 0) {
            this.linkedPositionsList = this.buildAllLinkedPositions(this.linkedPositionsList.flatMap((pos) => pos.items));
            this.cd.detectChanges();
          }
        },
        (error: any) => {
          this.toastr.error('Error occurred while getting all user forms for linked positions', error?.error?.message);
        }
      )
    );
  }

  createVaultUserFormLinkedPositions(item: any) {
    const payload = {
      vault_user_form_id: this.currentFormId,
      linked_vault_user_form_id: item.id,
      ...(item.owner_user_id && { owner_user_id: item.owner_user_id }),
      ...(item.owner_entity_id && { owner_entity_id: item.owner_entity_id }),
    };
    this.subscription.add(
      this.documentService.createVaultUserFormLinkedPositions(payload).subscribe(
        (response) => {
          this.getVaultUserFormLinkedPositions();
          this.getAllUserFormsForLinkedPositions();
        },
        (error: any) => {
          this.toastr.error('Error occurred while creating vault user form linked positions', error?.error?.message);
        }
      )
    );
  }

  updateVaultUserFormLinkedPositionsSetting({ key, value }: { key: string; value: boolean }) {
    const payload = {
      vault_user_form_id: this.currentFormId,
      [key]: value,
    };
    this.subscription.add(
      this.documentService.updateVaultUserFormLinkedPositionsSetting(payload).subscribe((response) => {
        this.linkedPositionsList.forEach((position) => {
          if (position.key === key) {
            position.autoAddIsEnabled = value;
          }
        });
        const autoAddIsEnabled = this.linkedPositionsList.some((position) => position.autoAddIsEnabled);
        if (autoAddIsEnabled) {
          this.getVaultUserFormLinkedPositions();
          this.getAllUserFormsForLinkedPositions();
        }
        this.cd.detectChanges();
      })
    );
  }

  deleteVaultUserFormLinkedPositions(selectedItems: any) {
    const selectedItemsId = selectedItems.map((item) => item.id);
    const payload = {
      vault_user_form_id: this.currentFormId,
      ids: selectedItemsId,
    };
    this.subscription.add(
      this.documentService.deleteVaultUserFormLinkedPositions(payload).subscribe((response) => {
        this.getVaultUserFormLinkedPositions();
        this.getAllUserFormsForLinkedPositions();
        this.subscription.add(this._notifyDocumentCreatedAddAsRolePlayers.sendMessage(response));
      })
    );
  }

  buildAllLinkedPositions(items: any[]): CategorizedTableData[] {
    const categorizedOwnedItems = { assets: [], liabilities: [], entities: [] as any[] };

    for (const item of items ?? []) {
      const asset = this.isTrueFlag(item?.is_asset);
      const liability = this.isTrueFlag(item?.is_liability);
      if (asset && !liability) {
        categorizedOwnedItems.assets.push(item);
      } else if (!asset && liability) {
        categorizedOwnedItems.liabilities.push(item);
      } else {
        categorizedOwnedItems.entities.push(item);
      }
    }
    const tableMap: { [key: string]: CategorizedTableData } = {
      assets: {
        key: 'assets',
        title: 'Linked Assets',
        searchBoxPlaceHolder: 'Link an asset',
        items: categorizedOwnedItems.assets,
        tableConfig: this.buildTableConfig('assets'),
        allUserInstanceForms: this.allFormsForAssets,
        linkedItems: categorizedOwnedItems.assets.map(({ formid, vault_user_form_user_id }) => ({
          id: formid,
          ...(vault_user_form_user_id && { user_id: vault_user_form_user_id }),
        })),
        ...(this.linkedAssetsEnabled !== null && { autoAddIsEnabled: Boolean(this.linkedAssetsEnabled) }),
      },
      liabilities: {
        key: 'liabilities',
        title: 'Linked Liabilities',
        searchBoxPlaceHolder: 'Link a liability',
        items: categorizedOwnedItems.liabilities,
        tableConfig: this.buildTableConfig('liabilities'),
        allUserInstanceForms: this.allFormsForLiabilities,
        linkedItems: categorizedOwnedItems.liabilities.map(({ formid, vault_user_form_user_id }) => ({
          id: formid,
          ...(vault_user_form_user_id && { user_id: vault_user_form_user_id }),
        })),
        ...(this.linkedLiabilitiesEnabled !== null && { autoAddIsEnabled: Boolean(this.linkedLiabilitiesEnabled) }),
      },
      entities: {
        key: 'entities',
        title: 'Linked Entities',
        searchBoxPlaceHolder: 'Link an entity',
        items: categorizedOwnedItems.entities,
        tableConfig: this.buildTableConfig('entities'),
        allUserInstanceForms: this.allFormsForEntities,
        linkedItems: categorizedOwnedItems.entities.map(({ formid, vault_user_form_user_id }) => ({
          id: formid,
          ...(vault_user_form_user_id && { user_id: vault_user_form_user_id }),
        })),
        ...(this.linkedEntitiesEnabled !== null && { autoAddIsEnabled: Boolean(this.linkedEntitiesEnabled) }),
      },
    };

    return this.tableDisplayOrder.map((key) => tableMap[key]);
  }

  openTemplate(file) {
    const constructedFile = {
      ...file,
      id: file.formid,
    };

    if (file.writeAccess && file.vault_user_form_user_id !== 'null') {
      const dialogOpen = this.dialog.open(AddDocumentComponent, {
        disableClose: true,
        data: {
          file: constructedFile,
          mode: 'edit',
          isintelligent: false,
          isfileListLoad: true,
          isFromChecklist: true,
          vufid: file.vault_user_form_id,
          vault_user_form_user_id: file.vault_user_form_user_id,
          isReport: true,
        },
        panelClass: 'full-window-dialog-add-document',
        autoFocus: false,
      });
      this.subscription.add(
        dialogOpen.afterClosed().subscribe((res) => {
          this.getVaultUserFormLinkedPositions();
        })
      );
    } else if (!file.writeAccess && file.vault_user_form_user_id != 'null') {
      this.dialog.open(NonAccessTemplateComponent, {
        panelClass: 'shared-doc-view-dialog',
        data: {
          template: file,
        },
      });
    }
  }

  onItemSelectionChangeEventEmitterHandler(item: any) {
    const mappedItem = {
      id: item.id,
      ...(item.owner_userid && { owner_user_id: item.owner_userid }),
      ...(item.owner_entityid && { owner_entity_id: item.owner_entityid }),
    };

    this.createVaultUserFormLinkedPositions(mappedItem);
  }

  onCellDataClickEventEmitterHandler(event: any): void {
    switch (event.action) {
      case 'openTemplate':
        if (!event.record?.vault_user_form_user_id) {
          return;
        }
        this.openTemplate(event.record);
        break;
      default:
        console.warn(`Unknown action: ${event.action}`);
    }
  }

  onRowActionClick(event: { action: string; rowIndex: number; record: any }): void {
    switch (event.action) {
      case 'delete':
        this.deleteVaultUserFormLinkedPositions([event.record]);
        break;
      default:
        console.warn(`Unknown action: ${event.action}`);
    }
  }

  onToggleChangeEventEmitterHandler(isChecked: boolean, category: any) {
    const keyMap = {
      assets: 'linked_assets_enabled',
      liabilities: 'linked_liabilities_enabled',
      entities: 'linked_entities_enabled',
    };
    const key = keyMap[category];
    this.updateVaultUserFormLinkedPositionsSetting({ key, value: isChecked });
  }

  buildTableConfig(category: 'assets' | 'liabilities' | 'entities'): TableConfig {
    const baseConfig = TABLE_CONFIG_BY_CATEGORY[category];
    if (this.isBuySellTemplate) {
      return {
        ...baseConfig,
        columns: baseConfig.columns.map((col) => (col.column_id === 'type' ? { ...col, column_id: 'owner', column_label: 'Owner' } : col)),
        propertyMap: {
          ...baseConfig.propertyMap,
          owner: 'owner_name',
        },
      };
    }

    return baseConfig;
  }

  isTrueFlag(type: any): boolean {
    return type === 1 || type === '1' || type === true || type === 'true';
  }

  onLinkedPositionSelectionChange(event: { record: any; index: number; isChecked: boolean; categoryKey: string }) {
    this.linkedPositionSelectionChange.emit(event);
    this.cd.detectChanges();
  }

  clearSelections() {
    if (this.commonOwnershipTableComponents) {
      this.commonOwnershipTableComponents.forEach((component) => {
        component.clearSelections();
      });
    }
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
